import { communicationStore } from '@yoop/whitelabel-manager-store';
import { apiClientStore } from '@yoop/whitelabel-api-client';
import type {
  AuthenticationResult,
  EventDetailRequestData,
  EventTokenActionData,
  EventTokenResendInviteData,
  GetConfigActionData,
  MyYoopFilterOptions,
  PortMessage,
} from '@yoop/whitelabel-data';
import { IFRAME_INIT_EVENT, type Response } from '@yoop/whitelabel-data';
import { IFRAME_MESSAGE_TYPE, YoopIframeOrigin } from '@yoop/whitelabel-data';
import { CommsMessageActions } from '@yoop/whitelabel-data';
import { onGetEventsData } from '@yoop/whitelabel-event';
import { appStore } from '@yoop/whitelabel-manager-store';
import { MyYoopActions } from '@yoop/whitelabel-my-yoop';
import { apiConfig } from './environments/api-environment';
import { getUserProfile, logoutUser } from './user/user-actions.helper';

const { messageChannel } = communicationStore.getState();
messageChannel.port1.onmessage = (message) => {
  switch (message.data?.action) {
    case CommsMessageActions.GetEvents: {
      onGetEventsData(message.data.key);
      break;
    }
    case CommsMessageActions.Tabs:
      MyYoopActions.onGetUserTabs(message.data.key);
      break;
    case CommsMessageActions.InPersonEventsList: {
      const _portMessage = message.data as PortMessage<MyYoopFilterOptions>;
      MyYoopActions.onGetInPersonEventList(message.data.key, _portMessage.data);
      break;
    }
    case CommsMessageActions.VirtualEventsList: {
      const _portMessage = message.data as PortMessage<MyYoopFilterOptions>;
      MyYoopActions.onGetVirtualEventList(message.data.key, _portMessage.data);
      break;
    }
    case CommsMessageActions.RequestedEventsList: {
      const _portMessage = message.data as PortMessage<MyYoopFilterOptions>;
      MyYoopActions.onGetRequestedEventList(message.data.key, _portMessage.data);
      break;
    }
    case CommsMessageActions.EventDetail: {
      const _portMessage = message.data as PortMessage<EventDetailRequestData>;
      MyYoopActions.onGetEventDetail(
        _portMessage.data.eventId,
        _portMessage.data.marketplaceCode,
        message.data.key,
      );
      break;
    }
    case CommsMessageActions.ResaleEventsList: {
      const _portMessage = message.data as PortMessage<MyYoopFilterOptions>;
      MyYoopActions.onGetResaleEventList(message.data.key, _portMessage.data);
      break;
    }
    case CommsMessageActions.AssignTokenToCurrentUser: {
      const _portMessage = message.data as PortMessage<EventTokenActionData>;
      MyYoopActions.onAssignTokenToCurrentUser(
        undefined,
        _portMessage.data.tokenId,
        _portMessage.key,
      );
      break;
    }
    case CommsMessageActions.ResendTokenInvite: {
      const _portMessage = message.data as PortMessage<EventTokenResendInviteData>;
      MyYoopActions.onResendTokenInvite(_portMessage.data.assignmentId, _portMessage.key);
      break;
    }
    case CommsMessageActions.ReturnToken: {
      const _portMessage = message.data as PortMessage<EventTokenActionData>;
      MyYoopActions.onReturnToken(_portMessage.data.tokenId, _portMessage.key);
      break;
    }
    case CommsMessageActions.RevokeToken: {
      const _portMessage = message.data as PortMessage<EventTokenActionData>;
      MyYoopActions.onRevokeToken(_portMessage.data.tokenId, _portMessage.key);
      break;
    }
    case CommsMessageActions.Authentication: {
      const resultData = message.data.data as AuthenticationResult;
      if (resultData?.authenticated) {
        const { apiClient } = apiClientStore.getState();
        apiClient.setAuthentication(resultData.authToken);
        appStore.getState().updateCurrentUser(resultData.profile);
      }
      break;
    }
    case CommsMessageActions.Logout: {
      onLogout((message.data as PortMessage).key);
      break;
    }
    case CommsMessageActions.GetConfig: {
      const _portMessage = message.data as PortMessage<GetConfigActionData>;
      const { sendDataMessage } = communicationStore.getState();
      sendDataMessage({
        action: CommsMessageActions.GetConfig,
        key: _portMessage.key,
        getData: async () => {
          const { apiClient } = apiClientStore.getState();
          const config = await apiClient.clientConfigController.getClientConfiguration(
            _portMessage.data.apiKey,
          );
          appStore.getState().updateClient(config.clientId);
          return config;
        },
      });
      break;
    }
  }
};

window.parent?.postMessage(
  {
    type: IFRAME_MESSAGE_TYPE,
    origin: YoopIframeOrigin.CommsManager,
    content: {
      action: IFRAME_INIT_EVENT,
    },
  },
  document.referrer,
  [messageChannel.port2],
);

apiClientStore.getState().initializeClient({
  baseURL: apiConfig.api,
  withCredentials: true,
  unauthorizedCallback: () => {
    appStore.getState().updateCurrentUser(undefined);
    communicationStore.getState().sendMessage({
      action: CommsMessageActions.Authentication,
      key: CommsMessageActions.Authentication,
      data: {
        authenticated: false,
      } as AuthenticationResult,
    });
  },
  onExtractResponseBody: (response) => {
    const authToken = response.headers?.['authorization-extension'];
    if (authToken && !response.config?.url?.includes('/auth/logout')) {
      apiClientStore.getState().apiClient.setAuthentication(authToken);
      communicationStore.getState().sendMessage({
        action: CommsMessageActions.UpdateAuthToken,
        key: CommsMessageActions.UpdateAuthToken,
        data: {
          authToken,
        },
      });
    }
  },
});

const onLogout = async (key: string) => {
  try {
    await logoutUser();
    communicationStore.getState().sendMessage({
      action: CommsMessageActions.Logout,
      key,
      data: {
        success: true,
      } as Response<any>,
    });
  } catch (error) {
    communicationStore.getState().sendMessage({
      action: CommsMessageActions.Logout,
      key,
      data: {
        success: false,
        error,
      } as Response<any>,
    });
  } finally {
    appStore.getState().updateCurrentUser(undefined);
  }
};

const url = new URL(window.location.href);

//Attempts to get user profile in case it was already logged in to .yoop.app
getUserProfile()
  .then((profile) => {
    appStore.getState().updateCurrentUser(profile);
    communicationStore.getState().sendMessage({
      action: CommsMessageActions.Authentication,
      key: CommsMessageActions.Authentication,
      data: {
        authenticated: true,
        profile,
      } as AuthenticationResult,
    });
  })
  .catch(() => {
    appStore.getState().updateCurrentUser(undefined);
    communicationStore.getState().sendMessage({
      action: CommsMessageActions.Authentication,
      key: CommsMessageActions.Authentication,
      data: {
        authenticated: false,
      } as AuthenticationResult,
    });
  });
